/**
 * VAR Explorer Configuration
 *
 * Configuration for Values at Risk Explorer using ConfigurableBaseExplorer
 * Issue: #289
 */

import {
  BaseExplorerConfig,
  BaseEntity,
  FieldTransformer
} from '../../../types/baseExplorerConfig';
import { VAREntity, VARCategory, VARStatus } from '../../../types/var';
import { varServiceAdapter } from './varServiceAdapter';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { VAR_COLUMNS } from '../../../shared/varSchema';
import { gridColumns } from '../../../shared/columnSchema';

/**
 * Extend VAREntity to work with BaseEntity interface
 */
export interface VAREntityEntry extends BaseEntity, VAREntity {
  _id: string;
}

/**
 * Field transformers for VAR data display
 */
const nameTransformer: FieldTransformer = {
  transform: (value: string) => value || 'Unnamed',
  reverseTransform: (value: string) => value || ''
};

const varTypeTransformer: FieldTransformer = {
  transform: (value: VARCategory) => {
    const typeMap: Record<string, string> = {
      'Infrastructure': 'Infrastructure',
      'Cultural Resource': 'Cultural Resource',
      'Natural Resource': 'Natural Resource'
    };
    return typeMap[value] || value || 'Unknown';
  },
  reverseTransform: (value: string) => value as VARCategory
};

const statusTransformer: FieldTransformer = {
  transform: (value: VARStatus) => {
    const statusMap: Record<string, string> = {
      'Active': 'Active',
      'Archived': 'Archived'
    };
    return statusMap[value] || value || 'Active';
  },
  reverseTransform: (value: string) => value as VARStatus
};

const regionTransformer: FieldTransformer = {
  transform: (value: string | null) => value || 'N/A',
  reverseTransform: (value: string) => value === 'N/A' ? null : value
};

const redFlagTransformer: FieldTransformer = {
  transform: (value: boolean) => value ? 'Yes' : 'No',
  reverseTransform: (value: string) => value === 'Yes'
};

const occupancyTransformer: FieldTransformer = {
  transform: (value: string | null) => {
    if (!value) return 'Unknown';
    return value;
  },
  reverseTransform: (value: string) => value === 'Unknown' ? null : value
};

const varIdTransformer: FieldTransformer = {
  transform: (value: string) => value || '',
  reverseTransform: (value: string) => value
};

const detailsTransformer: FieldTransformer = {
  transform: (value: string | null) => value || '',
  reverseTransform: (value: string) => value || null
};

const varSubtypeTransformer: FieldTransformer = {
  transform: (value: string | null) => value || '',
  reverseTransform: (value: string) => value || null
};

const keywordsTransformer: FieldTransformer = {
  transform: (value: string[]) => {
    if (!value || !Array.isArray(value)) return '';
    return value.join(', ');
  },
  reverseTransform: (value: string) => value ? value.split(',').map(s => s.trim()) : []
};

const sprinklerFlagTransformer: FieldTransformer = {
  transform: (value: { active: boolean; reason: string; name: string; sprinkerlocaldate: string }) =>
    value?.active ? 'Yes' : 'No',
  reverseTransform: (value: string) => ({
    active: value === 'Yes',
    reason: '',
    name: '',
    sprinkerlocaldate: ''
  })
};

const photosCountTransformer: FieldTransformer = {
  transform: (value: unknown[] | null | undefined) => {
    if (!value || !Array.isArray(value)) return '0';
    return String(value.length);
  },
  reverseTransform: (value: string) => [] // read-only column
};

const verificationDateTransformer: FieldTransformer = {
  transform: (value: { Date: string | null } | null) => {
    if (!value || !value.Date) return '';
    return new Date(value.Date).toLocaleDateString('en-CA');
  },
  reverseTransform: (value: string) => ({ Date: value || null, Method: '', Notes: '', VerifiedBy: '' })
};

/**
 * Create VAR Explorer configuration
 */
export function createVARConfig(): BaseExplorerConfig<VAREntityEntry> {
  return {
    entityName: 'Values at Risk',
    complexityLevel: 'complex',
    serviceAdapter: varServiceAdapter,
    displayFields: [
      'var_id',
      'name',
      'details',
      'var_type',
      'var_subtype',
      'region',
      'occupancy',
      'status',
      'keywords',
      'red_flag_active',
      'sprinklerFlag',
      'photos',
      'verification'
    ],
    fieldTransformers: {
      var_id: varIdTransformer,
      name: nameTransformer,
      details: detailsTransformer,
      var_type: varTypeTransformer,
      var_subtype: varSubtypeTransformer,
      region: regionTransformer,
      occupancy: occupancyTransformer,
      status: statusTransformer,
      keywords: keywordsTransformer,
      red_flag_active: redFlagTransformer,
      sprinklerFlag: sprinklerFlagTransformer,
      photos: photosCountTransformer,
      verification: verificationDateTransformer
    } as Record<keyof VAREntityEntry, FieldTransformer>,
    features: {
      searching: {
        enabled: true,
        searchFields: ['name', 'var_id', 'keywords'],
        placeholder: 'Search VAR by name, ID, or keywords...',
        debounceMs: 300
      },
      filtering: {
        enabled: true,
        advancedFilters: false,
        quickFilters: [
          {
            field: 'var_type',
            type: 'dropdown',
            label: 'Type',
            options: [
              { value: 'Infrastructure', label: 'Infrastructure' },
              { value: 'Cultural Resource', label: 'Cultural Resource' },
              { value: 'Natural Resource', label: 'Natural Resource' }
            ]
          },
          {
            field: 'status',
            type: 'dropdown',
            label: 'Status',
            options: [
              { value: 'Active', label: 'Active' },
              { value: 'Archived', label: 'Archived' }
            ]
          },
          {
            field: 'region',
            type: 'dropdown',
            label: 'Region',
            // FMD region codes (canonical, from VAR_REGIONS in src/types/var.ts)
            // plus an "Other" sentinel that surfaces legacy/non-FMD codes
            // (ED, EC, numeric IDs, NULL, etc.) so they can be triaged.
            // Backend translates __OTHER__ to a NOT IN + IS NULL clause.
            // Note: CBE's FilterPanel auto-prepends an "All" option, so we
            // don't include one here.
            options: [
              { value: 'EV', label: 'Beaufort Delta' },
              { value: 'FS', label: 'Deh Cho' },
              { value: 'ZF', label: 'North Slave' },
              { value: 'VQ', label: 'Sahtu' },
              { value: 'SM', label: 'South Slave' },
              { value: '__OTHER__', label: 'Other (non-FMD / legacy / blank)' },
            ]
          },
          {
            field: 'occupancy',
            type: 'dropdown',
            label: 'Occupancy',
            options: [
              { value: 'None', label: 'None' },
              { value: 'Periodic', label: 'Periodic' },
              { value: 'Year Round', label: 'Year Round' },
              { value: 'Unknown', label: 'Unknown' }
            ]
          },
          { field: 'var_id', label: 'VAR ID', type: 'textSearch' },
          { field: 'name', label: 'Title', type: 'textSearch' },
          { field: 'details', label: 'Details', type: 'textSearch' },
          { field: 'var_subtype', label: 'Subcategory', type: 'textSearch' },
          { field: 'keywords', label: 'Keywords', type: 'textSearch' },
          {
            field: 'red_flag_active', label: 'Red Flag', type: 'dropdown',
            options: [
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' }
            ]
          },
          {
            field: 'sprinklerFlag', label: 'Sprinkler', type: 'dropdown',
            options: [
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' }
            ]
          },
          {
            field: 'verification', label: 'Verified', type: 'dropdown',
            options: [
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' }
            ]
          }
        ]
      },
      sorting: {
        enabled: true,
        multiColumn: false,
        defaultSort: [{ field: 'name', direction: 'asc' }]
      },
      pagination: {
        enabled: true,
        defaultPageSize: 25,
        pageSizeOptions: [10, 25, 50, 100],
        showPageInfo: true
      },
      export: {
        enabled: true,
        formats: ['csv', 'excel'],
        includeFilters: true,
        entity: 'var'
      },
      editing: {
        enabled: false,
        mode: 'modal',
        allowCreate: false,
        allowUpdate: false,
        allowDelete: false
      }
    },
    presentation: {
      title: 'Values at Risk',
      subtitle: 'Protected sites, structures, and areas',
      icon: faTriangleExclamation,
      columnConfig: gridColumns(VAR_COLUMNS),
      styling: {
        theme: 'default',
        striped: true,
        bordered: true,
        hoverable: true
      },
      layout: {
        responsive: true,
        showFooter: true,
        showToolbar: true,
        minHeight: 400,
        maxHeight: 800
      }
    }
  };
}

// Default config export
export const varConfig = createVARConfig();
